/**
 * Stripe Provider Component
 * Wraps components that need access to Stripe Elements
 */

import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useApi } from '../../hooks/useApi';

// Context for Stripe state
const StripeContext = createContext(null);

/**
 * Stripe initialization singleton
 * Prevents race conditions by ensuring only one initialization happens
 * and all concurrent callers get the same promise
 */
const stripeInitState = {
  promise: null,       // The stripe promise
  initPromise: null,   // The initialization promise (to await)
  initialized: false,
};

/**
 * Initialize Stripe with proper race condition handling
 * @param {Function} apiGet - API getter function
 * @returns {Promise<{stripe: Promise, clientSecret: string}>}
 */
async function initializeStripe(apiGet) {
  // If already initialized, return cached values
  if (stripeInitState.initialized && stripeInitState.promise) {
    return { stripe: stripeInitState.promise };
  }

  // If initialization is in progress, wait for it
  if (stripeInitState.initPromise) {
    return stripeInitState.initPromise;
  }

  // Start initialization
  stripeInitState.initPromise = (async () => {
    try {
      const response = await apiGet('/api/billing/setup-intent');

      if (response.publishableKey) {
        stripeInitState.promise = loadStripe(response.publishableKey);
        stripeInitState.initialized = true;
      }

      return {
        stripe: stripeInitState.promise,
        clientSecret: response.clientSecret,
      };
    } finally {
      // Clear init promise so retries are possible on failure
      if (!stripeInitState.initialized) {
        stripeInitState.initPromise = null;
      }
    }
  })();

  return stripeInitState.initPromise;
}

/**
 * Hook to access Stripe context
 */
export function useStripeContext() {
  const context = useContext(StripeContext);
  if (!context) {
    throw new Error('useStripeContext must be used within a StripeProvider');
  }
  return context;
}

/**
 * StripeProvider Component
 * Initializes Stripe and provides Elements context
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 * @param {string} props.clientSecret - Optional client secret for SetupIntent/PaymentIntent
 */
function StripeProvider({ children, clientSecret: propClientSecret }) {
  const api = useApi();
  const [stripePromise, setStripePromise] = useState(stripeInitState.promise);
  const [clientSecret, setClientSecret] = useState(propClientSecret || null);
  const [loading, setLoading] = useState(!stripeInitState.initialized);
  const [error, setError] = useState(null);

  // Track if this component instance has started initialization
  const initStartedRef = useRef(false);

  // Initialize Stripe with publishable key
  useEffect(() => {
    // Skip if already initialized globally
    if (stripeInitState.initialized && stripeInitState.promise) {
      setStripePromise(stripeInitState.promise);
      setLoading(false);
      return;
    }

    // Skip if this component already started initialization
    if (initStartedRef.current) {
      return;
    }
    initStartedRef.current = true;

    async function initStripe() {
      try {
        // Use singleton initialization to prevent race conditions
        const result = await initializeStripe(api.get.bind(api));

        if (result.stripe) {
          setStripePromise(result.stripe);
        }

        if (result.clientSecret && !propClientSecret) {
          setClientSecret(result.clientSecret);
        }

        setLoading(false);
      } catch (err) {
        console.error('Failed to initialize Stripe:', err);
        setError(err.message || 'Failed to initialize payment system');
        setLoading(false);
        initStartedRef.current = false; // Allow retry
      }
    }

    initStripe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  // Update client secret if prop changes
  useEffect(() => {
    if (propClientSecret) {
      setClientSecret(propClientSecret);
    }
  }, [propClientSecret]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <CircularProgress size={24} sx={{ mr: 2 }} />
        <Typography variant="body2" sx={{
          color: "text.secondary"
        }}>
          Initializing payment system...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body1" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  if (!stripePromise) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body1" color="error">
          Payment system not available
        </Typography>
      </Box>
    );
  }

  // Stripe Elements options
  const elementsOptions = clientSecret
    ? {
        clientSecret,
        appearance: {
          theme: 'night',
          variables: {
            colorPrimary: '#1976d2',
            colorBackground: '#121212',
            colorText: '#ffffff',
            colorDanger: '#f44336',
            fontFamily: 'Roboto, sans-serif',
            borderRadius: '4px',
          },
          rules: {
            '.Input': {
              backgroundColor: '#1e1e1e',
              borderColor: 'rgba(255, 255, 255, 0.23)',
            },
            '.Input:focus': {
              borderColor: '#1976d2',
              boxShadow: '0 0 0 1px #1976d2',
            },
            '.Label': {
              color: 'rgba(255, 255, 255, 0.7)',
            },
          },
        },
      }
    : {
        appearance: {
          theme: 'night',
          variables: {
            colorPrimary: '#1976d2',
            colorBackground: '#121212',
            colorText: '#ffffff',
            colorDanger: '#f44336',
            fontFamily: 'Roboto, sans-serif',
            borderRadius: '4px',
          },
        },
      };

  const contextValue = {
    clientSecret,
    setClientSecret,
    refreshSetupIntent: async () => {
      try {
        const response = await api.get('/api/billing/setup-intent');
        if (response.clientSecret) {
          setClientSecret(response.clientSecret);
        }
        return response;
      } catch (err) {
        setError(err.message);
        throw err;
      }
    },
  };

  return (
    <StripeContext.Provider value={contextValue}>
      <Elements stripe={stripePromise} options={elementsOptions}>
        {children}
      </Elements>
    </StripeContext.Provider>
  );
}

export default StripeProvider;
